import { ROLE } from '@cmoh/shared'
import type React from 'react'
import { Outlet, type RouteObject } from 'react-router-dom'
import { AppSuspenseFallback } from '@/components/AppSuspenseFallback'
import { RouteErrorBoundary } from '@/components/shared/RouteErrorBoundary'
import { PermissionGate } from '@/lib/routing/routing.guard'
import type { AppRoute } from '@/lib/routing/routing.types'

const loadingFallback = <AppSuspenseFallback />

/**
 * Default `errorElement` for feature routes. Placed at each route (not only the
 * root) so a lazy/render failure replaces just the `<Outlet />` content while
 * `AppLayout` (sidebar + nav) stays mounted. A route can override it via
 * `AppRoute.errorElement`.
 */
const defaultErrorElement = <RouteErrorBoundary />

/**
 * Wraps a node with the layout of the route (if exists).
 */
function withLayout(
  node: React.ReactElement,
  layout: AppRoute['layout'] | undefined,
): React.ReactElement {
  if (!layout) return node
  const Layout = layout
  return <Layout>{node}</Layout>
}

/**
 * Applies the access gate defined in `meta.require`.
 *
 * Supported checks (evaluation order matches PermissionGate):
 * 1) `public`: if true, route is accessible without authentication
 * 2) `superAdminOnly`: if true, only super-admins can enter (ignores roles/minRole).
 * 3) `superAdminBypass`: if true (default), super-admin skips roles/minRole checks.
 * 4) `roles`: whitelist of exact roles.
 * 5) `minRole`: hierarchical minimum role.
 *
 * Notes:
 * - By default, all routes are private and require authentication
 * - If `meta.public` is true, no authentication is required
 * - If `meta.require` is present but neither `roles` nor `minRole` is provided,
 *   still mounts a PermissionGate shell with `minRole: ROLE.VIEWER` so the
 *   super-admin flags apply and unauthenticated users are redirected.
 */
function withGate(
  node: React.ReactElement,
  meta: AppRoute['meta'] | undefined,
): React.ReactElement {
  if (meta?.public === true) {
    return node
  }

  const req = meta?.require
  if (!req) {
    return (
      <PermissionGate minRole={ROLE.VIEWER} loadingFallback={loadingFallback}>
        {node}
      </PermissionGate>
    )
  }

  const common = {
    superAdminOnly: req.superAdminOnly ?? false,
    superAdminBypass: req.superAdminBypass ?? true,
  }

  // 1) Roles Whitelist
  if (req.roles && req.roles.length > 0) {
    return (
      <PermissionGate roles={req.roles} loadingFallback={loadingFallback} {...common}>
        {node}
      </PermissionGate>
    )
  }

  // 2) Minimum role (hierarchy)
  if (req.minRole) {
    return (
      <PermissionGate minRole={req.minRole} loadingFallback={loadingFallback} {...common}>
        {node}
      </PermissionGate>
    )
  }

  // Edge case: only superadmin flags without roles/minRole
  return (
    <PermissionGate minRole={ROLE.VIEWER} loadingFallback={loadingFallback} {...common}>
      {node}
    </PermissionGate>
  )
}

/**
 * Builds the `element` to render for a given route.
 * - If the route does not define `element`:
 *    - When it has children and/or meta.require and/or layout, we auto-insert an `<Outlet />`
 *      shell so index routes render correctly and the parent’s Gate/Layout still apply.
 *    - Otherwise (no children/require/layout), returns `null`.
 * - Composition order: Component -> Gate -> Layout
 */
function buildElement(route: AppRoute): React.ReactElement | null {
  const Comp = route.element
  if (!Comp) {
    const needsShell = Boolean(route.children?.length || route.meta?.require || route.layout)
    if (!needsShell) return null
    let node = <Outlet />
    node = withGate(node, route.meta)
    node = withLayout(node, route.layout)
    return node
  }

  let node = <Comp /> // Base component
  node = withGate(node, route.meta) // Permissions gate (RBAC)
  node = withLayout(node, route.layout) // Optional Route layout

  return node
}

/**
 * Transforms `AppRoute[]` (the DSL) into `RouteObject[]` (React Router).
 * Called from the central router setup (router.tsx) when building the router.
 * Notes:
 *  - Index routes return `{ index: true, element, ... }` (no `path`, no `children`).
 *  - Non-index routes require `path` and may have `children`.
 */
export function toRouteObjects(routes: Readonly<AppRoute[]>): RouteObject[] {
  return routes.map((r) => {
    const baseRoute = {
      element: buildElement(r),
      loader: r.loader,
      action: r.action,
      errorElement: r.errorElement ?? defaultErrorElement,
      handle: r.meta ? { meta: r.meta } : undefined,
    }

    if (r.index) {
      return {
        index: true,
        ...baseRoute,
      }
    }

    if (!r.path) {
      throw new Error(
        `[routing.compose] Non-index route is missing 'path'. Route meta title: ${r.meta?.title ?? '(no title)'}`,
      )
    }

    return {
      path: r.path,
      ...baseRoute,
      children: r.children ? toRouteObjects(r.children) : undefined,
    }
  })
}
